import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Radio, Calendar, Clock, PlayCircle, Loader2, ArrowRight, CheckCircle2, BellRing, BellPlus, Repeat } from 'lucide-react';
import PortalLayout from './PortalLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const muted = { color: 'var(--p-muted)' };

const fmtWhen = (iso) => {
  if (!iso) return 'Time TBA';
  try {
    return new Date(iso).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return iso; }
};

const STATUS_META = {
  live: { label: 'Live now', cls: { background: 'rgba(239,68,68,0.12)', color: '#DC2626' } },
  scheduled: { label: 'Upcoming', cls: { background: 'rgba(29,78,216,0.12)', color: '#1D4ED8' } },
  ended: { label: 'Replay', cls: { background: 'rgba(16,185,129,0.12)', color: '#059669' } },
};

const roomHref = (w) => `/webinar/${w.slug}${w.join_token ? `?t=${w.join_token}` : ''}`;

export default function ClientTraining() {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(null);

  const load = () => {
    axios.get(`${API_URL}/api/portal/webinars`, { withCredentials: true })
      .then((r) => setRooms(Array.isArray(r.data) ? r.data : []))
      .catch(() => setRooms([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const remindMe = async (w) => {
    setRegistering(w.room_id);
    try {
      const res = await axios.post(`${API_URL}/api/portal/webinars/${w.slug}/register`, {}, { withCredentials: true });
      setRooms((prev) => prev.map((x) => x.room_id === w.room_id ? { ...x, registered: true, join_token: res.data.join_token } : x));
      toast.success("You're registered. We'll email you a reminder before it starts.");
    } catch {
      toast.error('Could not register. Please try again.');
    } finally {
      setRegistering(null);
    }
  };

  return (
    <PortalLayout active="training">
      <div className="mb-8" data-testid="client-training-page">
        <span className="text-xs tracking-[0.2em] uppercase text-[#F97316] font-semibold p-eyebrow">Live Training</span>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-light mt-1" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="training-title">
          Trainings &amp; Workshops
        </h1>
        <p className="mt-2 max-w-2xl" style={muted}>Join David live for member-wide strategy trainings, right here in your portal, no extra logins. Register for an upcoming session and we'll remind you before it starts. Replays appear here afterward.</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#1D4ED8]" size={28} /></div>
      ) : rooms.length === 0 ? (
        <div className="p-card rounded-2xl p-10 text-center" data-testid="no-trainings">
          <Radio size={36} className="mx-auto mb-3" style={{ color: 'var(--p-faint)' }} />
          <p className="text-sm" style={muted}>No live trainings scheduled right now. Check back soon. New sessions are added regularly.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-5" data-testid="training-grid">
          {rooms.map((w) => {
            const meta = STATUS_META[w.status] || STATUS_META.scheduled;
            const isLive = w.status === 'live';
            const isReplay = w.status === 'ended';
            const isUpcoming = w.status === 'scheduled';
            return (
              <div key={w.room_id} className="p-card rounded-2xl p-6 flex flex-col" data-testid={`training-${w.slug}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>{w.title}</h3>
                  <span className="text-xs px-2.5 py-1 rounded-full font-medium whitespace-nowrap inline-flex items-center gap-1" style={meta.cls}>
                    {isLive && <span className="w-1.5 h-1.5 rounded-full bg-[#DC2626] animate-pulse" />}
                    {meta.label}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-3 text-sm mb-3" style={muted}>
                  {isReplay ? (
                    <span className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-emerald-500" /> Recording available</span>
                  ) : w.recurring ? (
                    <span className="flex items-center gap-1.5"><Repeat size={14} className="text-[#F97316]" /> {w.recurrence_label || 'Recurring'}</span>
                  ) : (
                    <span className="flex items-center gap-1.5"><Calendar size={14} className="text-[#1D4ED8]" />{fmtWhen(w.scheduled_at)}</span>
                  )}
                  {isUpcoming && w.registered && (
                    <span className="flex items-center gap-1.5 text-emerald-600" data-testid={`reminders-on-${w.slug}`}><BellRing size={14} /> Reminders on</span>
                  )}
                </div>
                {w.recurring && !isReplay && (
                  <p className="text-xs mb-3 flex items-center gap-1.5" style={muted}>
                    <Calendar size={12} className="text-[#1D4ED8]" /> Next session: {fmtWhen(w.scheduled_at)}
                  </p>
                )}
                {w.description && <p className="text-sm mb-5 line-clamp-3" style={muted}>{w.description}</p>}

                <div className="mt-auto flex flex-col gap-2">
                  {isUpcoming && !w.registered && (
                    <button
                      onClick={() => remindMe(w)}
                      disabled={registering === w.room_id}
                      className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg font-medium text-sm transition-colors bg-[#F97316] hover:bg-[#EA580C] text-white disabled:opacity-60"
                      data-testid={`training-remind-${w.slug}`}
                    >
                      {registering === w.room_id ? <Loader2 size={15} className="animate-spin" /> : <BellPlus size={15} />}
                      {registering === w.room_id ? 'Registering…' : 'Remind me'}
                    </button>
                  )}
                  <Link
                    to={roomHref(w)}
                    className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg font-medium text-sm transition-colors text-white ${isLive ? 'bg-[#DC2626] hover:bg-[#B91C1C]' : 'bg-[#1D4ED8] hover:bg-[#2563EB]'}`}
                    data-testid={`training-join-${w.slug}`}
                  >
                    {isLive ? <><Radio size={15} /> Join live now</> : isReplay ? <><PlayCircle size={15} /> Watch replay</> : <><Clock size={15} /> {w.registered ? 'Open training room' : 'View details'} <ArrowRight size={14} /></>}
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </PortalLayout>
  );
}
